import * as React from "react";
import "./assets/styles/css/App.css";
import { BrowserRouter, useLocation } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { useApp } from "./hooks/useApp";
import Loader from "./components/Loader";
import CssBaseline from "@mui/material/CssBaseline";
import { TAuthState } from "./types/Auth";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "./store/Store";
import { useEffect, useState } from 'react';
import { TDispatch } from "./types/Thunk";
import { getAppSettings } from "./actions/AuthActions";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import ErrorPage from "./components/ErrorPage";
import { TErrorState } from "./types/Error";
import AppRoutes from "./AppRoutes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import UploadsComponent from './UploadsComponent';
import { useBeforeunload } from 'react-beforeunload';
import PullToRefresh from 'pulltorefreshjs';
import "./hooks/usePWAHandler";
const queryClient = new QueryClient();

const App: React.FC = () => {
  const { app } = useApp();
  const dispatch = useDispatch<TDispatch>();
  const { t, i18n } = useTranslation();
  const auth: TAuthState = useSelector<IAppState, TAuthState>(
    (state: IAppState) => state.authState
  );
  const errorState: TErrorState = useSelector<IAppState, TErrorState>(
    (state: IAppState) => state.errorState
  );

  const [loading, setLoading] = useState(true)

  function TranslationManager() {
    const location = useLocation();

    useEffect(() => {
      const isSurveyPage =
        location.pathname.includes('/survey/') ||
        (location.pathname.includes('/inspection/') && !location.pathname.includes('/inspection/media'));
      const metaId = 'dynamic-no-translate';

      if (isSurveyPage) {
        document.documentElement.classList.add('notranslate');
        document.documentElement.setAttribute('translate', 'no');

        if (!document.getElementById(metaId)) {
          const meta = document.createElement('meta');
          meta.name = "google";
          meta.content = "notranslate";
          meta.id = metaId;
          document.head.appendChild(meta);
        }
      } else {
        document.documentElement.classList.remove('notranslate');
        document.documentElement.removeAttribute('translate');
        document.getElementById(metaId)?.remove();
      }
    }, [location]);

    return null;
  }

  useEffect(() => {
    (async () => {
      await dispatch(getAppSettings()).then((res: AxiosResponse<any>) => {
        i18n.changeLanguage(res.data.languageCode);
        setLoading(false);
      });
    })();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isInWebAppiOS = window.navigator.standalone === true;

    if (isInWebAppiOS) {
      PullToRefresh.init({
        mainElement: 'body',
        distThreshold: 120,
        distMax: 140,
        instructionsPullToRefresh: t("generic.pull_to_refresh"),
        instructionsReleaseToRefresh: t("generic.release_to_refresh"),
        onRefresh() {
          window.location.reload();
        },
      });
    }

    return () => {
      if (isInWebAppiOS) {
        PullToRefresh.destroyAll();
      }
    };
  }, [t]);

  useBeforeunload((event) => {
    const currentPath = window.location.pathname;

    if (
      currentPath.includes('/survey/') &&
      !currentPath.includes('/end')
    ) {
      return 'Are you sure you want to leave this page?';
    }

    return null;
  });

  if (auth.loading || loading) {
    return <Loader visible={true} />;
  }

  if (errorState.code && errorState.message && errorState.code !== 401) {
    return <ErrorPage message={errorState.message} />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename="/app">
          <TranslationManager/>
          <UploadsComponent>
            <CssBaseline />
            <Loader visible={app.isLoading()} />
            <AppRoutes />
          </UploadsComponent>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
